import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { Users, Copy, CheckCircle, Share2, MessageCircle, Send, Twitter, Facebook } from 'lucide-react';

export default function ReferralPage() {
  const { user } = useAuth();
  const [directs, setDirects] = useState([]);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState('direct');
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [ranks, setRanks] = useState([]);

  const referralLink = `${window.location.origin}/register/${user?.referralCode}`;
  const shareText = `Join UniqueAiTarde – automated MT5 trading platform! Use my referral link to register and earn commissions. ${referralLink}`;

  useEffect(() => {
    axios.get('/api/referral/direct').then(r => setDirects(r.data.directs));
    axios.get('/api/referral/team').then(r => setTeam(r.data.team));
    axios.get('/api/admin/ranks').catch(() => {}).then(r => r && setRanks(r.data?.ranks || []));
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const shareOptions = [
    {
      name: 'WhatsApp', icon: '💬', color: 'bg-green-600',
      url: `https://wa.me/?text=${encodeURIComponent(shareText)}`
    },
    {
      name: 'Telegram', icon: '✈️', color: 'bg-blue-500',
      url: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent('Join UniqueAiTarde – automated MT5 trading!')}`
    },
    {
      name: 'Twitter', icon: '🐦', color: 'bg-sky-500',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`
    },
    {
      name: 'Facebook', icon: '👥', color: 'bg-blue-700',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`
    },
    {
      name: 'Email', icon: '📧', color: 'bg-gray-600',
      url: `mailto:?subject=Join UniqueAiTarde&body=${encodeURIComponent(shareText)}`
    },
    {
      name: 'SMS', icon: '📱', color: 'bg-purple-600',
      url: `sms:?body=${encodeURIComponent(shareText)}`
    },
  ];

  // Use native share if available
  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: 'Join UniqueAiTarde', text: shareText, url: referralLink })
        .catch(() => setShowShare(true));
    } else {
      setShowShare(true);
    }
  };

  const defaultRanks = [
    { level:1, name:'Bronze', directRequired:3, teamRequired:10, setupFeeCommission:20, profitCommission:10 },
    { level:2, name:'Silver', directRequired:5, teamRequired:50, setupFeeCommission:25, profitCommission:15 },
    { level:3, name:'Gold', directRequired:5, teamRequired:150, setupFeeCommission:30, profitCommission:20 },
    { level:4, name:'Platinum', directRequired:5, teamRequired:500, setupFeeCommission:35, profitCommission:25 },
    { level:5, name:'Diamond', directRequired:5, teamRequired:1500, setupFeeCommission:40, profitCommission:30 },
    { level:6, name:'Ruby', directRequired:5, teamRequired:5000, setupFeeCommission:45, profitCommission:35 },
    { level:7, name:'Crown', directRequired:5, teamRequired:11000, setupFeeCommission:50, profitCommission:40 },
  ];
  const displayRanks = ranks.length > 0 ? ranks : defaultRanks;
  const teamOnly = user ? team.filter(m => m.referredBy?.toString() !== user._id.toString()) : [];

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-2">My Team</h1>
        <p className="text-gray-400 mb-6">Grow your referral network and earn commissions.</p>

        {/* Stats Row — direct vs team separate */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{user?.directReferrals || 0}</p>
            <p className="text-gray-400 text-sm mt-1">Direct Referrals</p>
            <p className="text-gray-600 text-xs mt-0.5">Level-1 referrals only</p>
          </div>
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{user?.teamReferrals || 0}</p>
            <p className="text-gray-400 text-sm mt-1">Team Referrals</p>
            <p className="text-gray-600 text-xs mt-0.5">Downline beyond direct referrals</p>
          </div>
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-primary-400">L{user?.level || 0}</p>
            <p className="text-gray-400 text-sm mt-1">Current Level</p>
          </div>
        </div>

        {/* Referral Link Card */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
          <h3 className="text-white font-semibold mb-3">Your Referral Link</h3>
          <div className="flex gap-2">
            <div className="flex-1 bg-dark-600 border border-white/10 rounded-xl px-4 py-3 text-gray-300 text-sm font-mono truncate">
              {referralLink}
            </div>
            {/* Change 1: Copy button */}
            <button onClick={copyLink}
              className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-3 rounded-xl flex items-center gap-2 transition-colors flex-shrink-0">
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            {/* Change 1: Share button */}
            <button onClick={handleNativeShare}
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-3 rounded-xl flex items-center gap-2 transition-colors flex-shrink-0">
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Share</span>
            </button>
          </div>

          {/* Share Options Panel */}
          {showShare && (
            <div className="mt-4 p-4 bg-dark-600 rounded-xl border border-white/10 animate-slide-up">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-sm font-medium">Share via</p>
                <button onClick={() => setShowShare(false)} className="text-gray-400 hover:text-white text-xs">✕ Close</button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {shareOptions.map(opt => (
                  <a key={opt.name} href={opt.url} target="_blank" rel="noreferrer"
                    className={`${opt.color} hover:opacity-90 text-white rounded-xl py-2.5 px-2 text-center flex flex-col items-center gap-1 transition-opacity`}>
                    <span className="text-xl">{opt.icon}</span>
                    <span className="text-xs font-medium">{opt.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Note for inactive users */}
          {!user?.isActive && (
            <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-300 text-xs flex items-center gap-2">
              ⚠️ Your account is inactive. Only active users can refer new members.
            </div>
          )}
        </div>

        {/* Commission Structure */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
          <h3 className="text-white font-semibold mb-1">Commission Structure</h3>
          <p className="text-gray-500 text-xs mb-4">Direct referral: 15% fixed. Upline gets differential commission.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Level</th>
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Direct/Team</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Setup Fee %</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Profit Share %</th>
                  <th className="text-center py-2 px-3 text-gray-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {displayRanks.sort((a,b)=>a.level-b.level).map(r => {
                  const achieved = (user?.level || 0) >= r.level;
                  const isCurrent = (user?.level || 0) === r.level;
                  return (
                    <tr key={r.level} className={`border-b border-white/5 ${isCurrent ? 'bg-primary-500/10' : ''}`}>
                      <td className="py-2.5 px-3">
                        <span className={`font-bold ${achieved ? 'text-primary-400' : 'text-gray-500'}`}>
                          {r.name || `Level ${r.level}`}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{r.directRequired} / {(r.teamRequired||0).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`font-semibold ${achieved ? 'text-primary-300' : 'text-gray-500'}`}>{r.setupFeeCommission}%</span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`font-semibold ${achieved ? 'text-green-300' : 'text-gray-500'}`}>{r.profitCommission}%</span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {achieved
                          ? <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-0.5 rounded-full">{isCurrent ? 'Current' : '✓'}</span>
                          : <span className="bg-dark-600 text-gray-500 text-xs px-2 py-0.5 rounded-full">Locked</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Team Tabs */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="flex border-b border-white/5">
            <button onClick={() => setTab('direct')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'direct' ? 'text-primary-400 border-b-2 border-primary-500' : 'text-gray-400 hover:text-white'}`}>
              Direct ({directs.length})
            </button>
            <button onClick={() => setTab('team')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'team' ? 'text-primary-400 border-b-2 border-primary-500' : 'text-gray-400 hover:text-white'}`}>
              Team ({teamOnly.length})
            </button>
          </div>
          <div className="divide-y divide-white/5">
            {(tab === 'direct' ? directs : teamOnly).length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No {tab === 'direct' ? 'direct referrals' : 'team referrals'} yet</p>
              </div>
            ) : (tab === 'direct' ? directs : teamOnly).map(m => (
              <div key={m._id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold text-sm flex-shrink-0">
                  {m.name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{m.name}</p>
                  <p className="text-gray-500 text-xs truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`w-2 h-2 rounded-full ${m.isActive ? 'bg-primary-500' : 'bg-red-500'}`}></span>
                  <span className="text-xs bg-dark-600 text-gray-300 px-2 py-0.5 rounded-full">L{m.level}</span>
                  <span className="text-xs text-gray-500 hidden sm:block">{new Date(m.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
